import { Box, Grid2, Typography, useColorScheme } from "@mui/material"
import React, { useState, useEffect } from "react"

const ValueProp3: React.FC = () => {
  const { mode, systemMode } = useColorScheme();
  const [svgUrls, setSvgUrls] = useState<{[key: string]: string | null}>({
    ImgAsvg: null,
    ImgBsvg: null,
    ImgCsvg: null,
    ImgDsvg: null,
  });
  const [loading, setLoading] = useState(true);

  const isLightMode = ( mode === 'light' || (mode === 'system' && systemMode === 'light'));

  useEffect(() => {
    const loadSVGs = async () => {
      setLoading(true);
      const themeFolder = isLightMode ? 'light_mode' : 'dark_mode';

      try {
        // Dynamic import of SVG files as URLs
        const [a, b, c, d] = await Promise.all([
          import(`./assets/${themeFolder}/value_prop_3_a.svg`),
          import(`./assets/${themeFolder}/value_prop_3_b.svg`),
          import(`./assets/${themeFolder}/value_prop_3_c.svg`),
          import(`./assets/${themeFolder}/value_prop_3_d.svg`),
        ]);

        setSvgUrls({
          ImgAsvg: a.default,
          ImgBsvg: b.default,
          ImgCsvg: c.default,
          ImgDsvg: d.default,
        });
      } catch (error) {
        console.error('Failed to load SVGs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSVGs();
  }, [isLightMode]);

  const CardItems = [
    {
      id: "vp3-1",
      imageSrc: svgUrls.ImgAsvg,
      title: "Increase fan engagement",
      description: "Your AI persona responds to questions in your voice and style, keeping fans active and invested around the clock."
    },
    {
      id: "vp3-2",
      imageSrc: svgUrls.ImgBsvg,
      title: "Stay top-of-mind without burning out",
      description: "You stay visible and helpful even when you're offline, without having to spend your day in DMs or on camera."
    },
    {
      id: "vp3-3",
      imageSrc: svgUrls.ImgCsvg,
      title: "Earn passive income",
      description: "Sell subscriptions to your AI persona. Sell Courses. Your AI persona can sell your products and content for you!"
    },
    {
      id: "vp3-4",
      imageSrc: svgUrls.ImgDsvg,
      title: "Build a library of insights",
      description: "Every fan question helps you learn what your audience wants most, struggles with, and how they can be best served."
    },
  ]

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: "lg",
          width: '100%',
          gap: {xs: 3, md: 4},
          px: {xs: 1.5, md: 5},
          py: {xs: 3, md: 4},
        }}
      >
        <Typography variant="h5">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: "lg",
        gap: {xs: 3, md: 4},
        px: {xs: 1.5, md: 5},
        py: {xs: 3, md: 4},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h5">
          Scale your influence. Multiply your impact.
        </Typography>
        <Typography variant="body1" color="text.secondary"
          sx={{
            maxWidth: '440px',
          }}
        >
          Your AI persona keeps engaging fans and generating revenue long after
          you've logged off.
        </Typography>
      </Box>

      <Grid2
        container
        spacing={{xs: 0, md: 3}}
      >
        {
          CardItems.map((item) =>
            <CardItem
              key={item.id}
              id={item.id}
              imageSrc={item.imageSrc}
              title={item.title}
              description={item.description}
            />
          )
        }
      </Grid2>
    </Box>
  )
}

export default ValueProp3;

interface CardItemProps {
  id: string;
  imageSrc: string | null;
  title: string;
  description: string;
}

const CardItem: React.FC<CardItemProps> = ({id, imageSrc, title, description}) => {
  return (
    <Grid2 id={id}
      size={{xs: 12, laptop: 6}}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: {xs: 1.5, sm: 3},
        py: 3,
        borderRadius: 3,
        backgroundColor: (theme) => theme.palette.background["container-1"],
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h6">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          minHeight: 120,
        }}
      >
        {imageSrc ? (
          <Box
            component="img"
            src={imageSrc}
            alt={title}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        ) : (
          <Box
            sx={{
              width: 100,
              height: 100,
              backgroundColor: (theme) => theme.palette.action.hover,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        )}
      </Box>
    </Grid2>
  )
}
